import React, { useEffect, useState } from 'react';
import { legacyFincetCommandesApi } from '../services/legacyFincetCommandesApi';
import { ordersApi } from 'features/orders/services/ordersApi';
import { ExternalStatus, STATUS_OPTIONS } from '../types/types';
import { legacyFincetApi } from 'legacy/services/legacyFincetApi';

// Type d'une commande legacy
export interface LegacyCommandeData {
	legacyId: number;
	label: string;
	externalKey?: string;
	externalStatus: ExternalStatus;
	corelysId?: number;
	fournisseur?: {
		legacyId: number;
		externalKey: string;
		externalStatus: string;
		label: string;
	} | null;
}

interface Props {
	token: string;
}

export default function LegacyCommandesTable({ token }: Props) {
	const [commandes, setCommandes] = useState<LegacyCommandeData[]>([]);
	const [loading, setLoading] = useState(true);

	// Statuts filtrés (cases à cocher)
	const [statusFilter, setStatusFilter] = useState<ExternalStatus[]>(
		STATUS_OPTIONS.filter((s) => s !== 'IGNORED')
	);
	const [yearFilter, setYearFilter] = useState<'all' | number>(2025);
	const availableYears = [2025, 2024, 2023, 2022, 2021];

	// Chargement des commandes depuis l'API
	useEffect(() => {
		legacyFincetCommandesApi
			.list(token)
			.then(setCommandes)
			.finally(() => setLoading(false));
	}, [token]);

	// Changer le status dans le state
	const handleStatusChange = (index: number, newStatus: ExternalStatus) => {
		const copy = [...commandes];
		copy[index].externalStatus = newStatus;
		setCommandes(copy);
	};

	// Actions
	const handleImport = (legacyId: number) =>
		legacyFincetCommandesApi
			.import(legacyId, token)
			.then((data) => {
				if (data.corelysId) {
					alert(`Importé avec succès, Corelys ID: ${data.corelysId}`);
					// Met à jour le state
					setCommandes((prev) =>
						prev.map((c) =>
							c.legacyId === legacyId
								? {
										...c,
										externalKey: `${data.corelysId}`,
										externalStatus: 'IMPORTED',
									}
								: c
						)
					);
				}
			})
			.catch((err) => {
				// Essayer d'extraire le JSON du message
				const message = err.message || err;
				const legacyIdMatch = message.match(/ID: (\d+)/);
				if (legacyIdMatch) {
					const legacyId = parseInt(legacyIdMatch[1], 10);
					// Proposer un clic pour importer
					const doImport = window.confirm(
						`${message}\n\nVoulez-vous importer ce fournisseur maintenant ?`
					);
					if (doImport) {
						legacyFincetApi
							.import(legacyId, token)
							.then((data) => {
								if (data.corelysId) {
									alert(
										`Fournisseur importé avec succès, Corelys ID: ${data.corelysId}. Vous pouvez maintenant réessayer d'importer la commande.`
									);
								}
							})
							.catch((err) => alert(`Erreur réseau lors de l'import: ${err}`));
					}
				} else {
					alert(`Erreur réseau: ${message}`);
				}
			});

	const handleImportFournisseur = (
		legacySupplierId: number,
		legacyOrderId: number
	) =>
		legacyFincetApi
			.import(legacySupplierId, token)
			.then((data) => {
				if (data.corelysId) {
					alert(
						`Fournisseur importé avec succès, Corelys ID: ${data.corelysId}`
					);
					// Met à jour le state
					setCommandes((prev) =>
						prev.map((c) =>
							c.legacyId === legacyOrderId
								? {
										...c,
										fournisseur: {
											legacyId: legacySupplierId,
											externalKey: `${data.corelysId}`,
											externalStatus: 'IMPORTED',
											label: c.fournisseur?.label ?? '',
										},
									}
								: c
						)
					);
				}
			})
			.catch((err) => alert(`Erreur lors de l'import: ${err}`));

	const handleAssociate = (legacyId: number) => {
		const corelysId = prompt('ID Corelys ?');
		if (corelysId) {
			legacyFincetCommandesApi
				.update(legacyId, { externalKey: `${corelysId}` }, token)
				.then(() => {
					alert(`Associé à ${corelysId}`);
					// Met à jour le state
					setCommandes((prev) =>
						prev.map((c) =>
							c.legacyId === legacyId
								? {
										...c,
										externalKey: `${corelysId}`,
										externalStatus: 'MANUALLY_LINKED',
									}
								: c
						)
					);
				});
		}
	};

	const handleUnlink = (legacyId: number) => {
		if (
			window.confirm(
				'Confirmer la suppression de cette association ? La commande ne sera pas supprimée dans Corelys.'
			)
		) {
			legacyFincetCommandesApi.delete(legacyId, token).then(() => {
				alert(`Association supprimée`);
				// Met à jour le state
				setCommandes((prev) =>
					prev.map((c) =>
						c.legacyId === legacyId
							? {
									...c,
									externalKey: undefined,
									externalStatus: 'NOT_IMPORTED',
								}
							: c
					)
				);
			});
		}
	};

	const handleDelete = (
		localKey: number | string | undefined,
		legacyId: number
	) => {
		if (
			window.confirm(
				'Confirmer la suppression de la commande ? La commande sera dissociée.'
			)
		) {
			const lk = typeof localKey === 'string' ? Number(localKey) : localKey;
			if (lk == null || Number.isNaN(lk)) {
				alert(
					'Clé locale introuvable ou invalide, impossible de supprimer la commande dans Corelys.'
				);
				return;
			}

			ordersApi
				.delete(lk, token)
				.then(() => {
					alert(`Commande supprimée dans Corelys`);
					legacyFincetCommandesApi.delete(legacyId, token).then(() => {
						alert(`Association supprimée`);
						// Met à jour le state
						setCommandes((prev) =>
							prev.map((c) =>
								c.legacyId === legacyId
									? {
											...c,
											externalKey: undefined,
											externalStatus: 'NOT_IMPORTED',
										}
									: c
							)
						);
					});
				})
				.catch((err) => {
					alert(`Erreur lors de la suppression dans Corelys: ${err}`);
				});
		}
	};

	const toggleStatusFilter = (status: ExternalStatus) => {
		setStatusFilter((prev) =>
			prev.includes(status)
				? prev.filter((s) => s !== status)
				: [...prev, status]
		);
	};

	if (loading) return <p>Chargement...</p>;

	const parseLabel = (label: string) => {
		const match = label.match(/^(\d+)\/(\d{2})/);
		if (!match) return [0, 0];
		const compteur = parseInt(match[1], 10);
		let annee = parseInt(match[2], 10);
		// Convertir AA → 20AA pour comparaison simple
		annee += 2000;
		return [annee, compteur];
	};

	// Filtrage selon cases cochées et année
	const filteredCommandes = commandes
		.filter((c) => c.label)
		.filter((c) => statusFilter.includes(c.externalStatus))
		.filter((c) => {
			if (yearFilter === 'all') return true;
			const [annee] = parseLabel(c.label);
			return annee === yearFilter;
		})
		.sort((a, b) => {
			const [anneeA, compteurA] = parseLabel(a.label);
			const [anneeB, compteurB] = parseLabel(b.label);
			if (anneeA !== anneeB) return anneeA - anneeB;
			return compteurA - compteurB;
		});

	return (
		<div>
			<div className="flex items-center justify-between mb-4">
				<div className="flex gap-4">
					{STATUS_OPTIONS.map((status) => (
						<label key={status} className="flex items-center gap-1">
							<input
								type="checkbox"
								checked={statusFilter.includes(status)}
								onChange={() => toggleStatusFilter(status)}
							/>
							{status}
						</label>
					))}
				</div>

				<div className="flex items-center gap-4">
					{/* Select années */}
					<select
						value={yearFilter}
						onChange={(e) =>
							setYearFilter(
								e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10)
							)
						}
						className="border px-2 py-1"
					>
						<option value="all">Toutes les années</option>
						{availableYears.map((y) => (
							<option key={y} value={y}>
								{y}
							</option>
						))}
					</select>

					{/* Nombre de lignes */}
					<div className="text-gray-700 font-medium">
						{filteredCommandes.length} ligne
						{filteredCommandes.length > 1 ? 's' : ''}
					</div>
				</div>
			</div>
			<table className="w-full border border-gray-300">
				<thead className="bg-gray-100">
					<tr>
						<th className="p-2 border">Label</th>
						<th className="p-2 border">Legacy Key</th>
						<th className="p-2 border">Local Key</th>
						<th className="p-2 border">Status</th>
						<th className="p-2 border">Actions</th>
						<th className="p-2 border">Fournisseur</th>
					</tr>
				</thead>
				<tbody>
					{filteredCommandes.map((c, idx) => {
						let rowClass = 'hover:bg-gray-50'; // classe par défaut
						if (
							c.externalStatus === 'IMPORTED' ||
							c.externalStatus === 'MANUALLY_LINKED'
						)
							rowClass += ' bg-green-100';
						else if (c.externalStatus === 'NOT_IMPORTED')
							rowClass += ' bg-yellow-100';
						else if (c.externalStatus === 'IGNORED') rowClass += ' bg-red-100';

						return (
							<tr key={c.legacyId} className={rowClass}>
								<td className="p-2 border">{c.label}</td>
								<td className="p-2 border">{c.legacyId}</td>
								<td className="p-2 border">
									{c.externalKey ? (
										<a
											href={`/app/orders/${c.externalKey}/manage`}
											className="text-blue-600 underline"
										>
											{c.externalKey}
										</a>
									) : (
										'-'
									)}
								</td>
								<td className="p-2 border">
									<select
										value={c.externalStatus}
										onChange={(e) =>
											handleStatusChange(idx, e.target.value as ExternalStatus)
										}
									>
										{STATUS_OPTIONS.map((s) => (
											<option key={s} value={s}>
												{s}
											</option>
										))}
									</select>
								</td>
								<td className="p-2 border flex gap-2">
									{(c.externalStatus === 'NOT_IMPORTED' ||
										c.externalStatus === 'IGNORED') && (
										<>
											<button onClick={() => handleImport(c.legacyId)}>
												Importer
											</button>
											<button onClick={() => handleAssociate(c.legacyId)}>
												Associer
											</button>
										</>
									)}
									{(c.externalStatus === 'IMPORTED' ||
										c.externalStatus === 'MANUALLY_LINKED') && (
										<>
											<button
												onClick={() => handleDelete(c.externalKey, c.legacyId)}
											>
												Supprimer
											</button>
											<button onClick={() => handleUnlink(c.legacyId)}>
												Dissocier
											</button>
										</>
									)}
								</td>
								<td className="p-2 border">
									{c.fournisseur?.externalStatus === 'IMPORTED' ? (
										<span className="text-green-400">
											{c.fournisseur?.label}
										</span>
									) : (
										<span className="text-red-400">
											{c.fournisseur?.label}{' '}
											<button
												onClick={() =>
													handleImportFournisseur(
														c.fournisseur?.legacyId ?? 0,
														c.legacyId
													)
												}
											>
												(importer)
											</button>
										</span>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
